import { useApi } from '@hooks/useApi';
import { Order, OrderType } from '@protos/v2/order';
import { createContext, useContext, useState, useCallback } from 'react';
import { useAsync } from 'react-use';

export interface OrderNotification {
  id: string;
  order: Order;
  timestamp: number;
  read: boolean;
}

interface OrderNotificationsContextType {
  notifications: OrderNotification[];
  addNotification: (order: Order) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
  clearNotification: (id: string) => void;
  isSoundEnabled: boolean;
  toggleSound: () => void;
  selectedOrderTypes: OrderType[];
  updateSelectedOrderTypes: (types: OrderType[]) => void;
}

const OrderNotificationsContext = createContext<OrderNotificationsContextType | undefined>(undefined);

export function OrderNotificationsProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<OrderNotification[]>([]);
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const [selectedOrderTypes, setSelectedOrderTypes] = useState<OrderType[]>(Object.values(OrderType));
  const { apiClient } = useApi();

  useAsync(async () => {
    if (!apiClient) return;
    const userSettings = await apiClient.getUserSettings();
    if (userSettings?.ocl_trading?.orderNotifications?.notificationList?.length) {
      apiClient.updateUserSettings({
        ...userSettings,
        ocl_trading: {
          ...userSettings.ocl_trading,
          orderNotifications: {
            ...userSettings.ocl_trading.orderNotifications,
            notificationList: [],
          },
        },
      });
    }
  }, [apiClient]);

  const addNotification = useCallback((order: Order) => {
    setNotifications(prev => [
      {
        id: order.id,
        order,
        timestamp: Date.now(),
        read: false,
      },
      ...prev,
    ]);
  }, []);

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev => prev.map(notification => (notification.id === id ? { ...notification, read: true } : notification)));
  }, []);

  const toggleSound = useCallback(() => {
    setIsSoundEnabled(prev => !prev);
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(notification => ({ ...notification, read: true })));
  }, []);

  const clearNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const updateSelectedOrderTypes = useCallback((types: OrderType[]) => {
    setSelectedOrderTypes(types);
  }, []);

  return (
    <OrderNotificationsContext.Provider
      value={{
        notifications,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearNotifications,
        clearNotification,
        isSoundEnabled,
        toggleSound,
        selectedOrderTypes,
        updateSelectedOrderTypes,
      }}
    >
      {children}
    </OrderNotificationsContext.Provider>
  );
}

export function useOrderNotifications() {
  const context = useContext(OrderNotificationsContext);
  if (!context) {
    throw new Error('useOrderNotifications must be used within OrderNotificationsProvider');
  }
  return context;
}
